import React, { useState, useEffect, useRef } from 'react';
import { announcementAPI } from '../../services/api';
import './AnnouncementAdmin.css';

const AnnouncementAdmin = () => {
  const [isActive, setIsActive] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const editorRef = useRef(null);

  useEffect(() => {
    announcementAPI.get()
      .then(r => {
        const html = r.data.text || '';
        if (editorRef.current) editorRef.current.innerHTML = html;
        setPreviewHtml(html);
        setIsActive(r.data.is_active || false);
      })
      .catch(() => {});
  }, []);

  const syncPreview = () => {
    setPreviewHtml(editorRef.current?.innerHTML || '');
  };

  // Prevent toolbar buttons from stealing focus (which would lose the selection)
  const hold = (e) => e.preventDefault();

  const applyBold = () => {
    document.execCommand('bold', false, null);
    syncPreview();
  };

  const applySize = (sizePx) => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount || sel.isCollapsed) return;
    const range = sel.getRangeAt(0);
    const fragment = range.extractContents();
    const span = document.createElement('span');
    span.style.fontSize = sizePx;
    span.appendChild(fragment);
    range.insertNode(span);
    // Keep the new span selected
    sel.removeAllRanges();
    const r2 = document.createRange();
    r2.selectNodeContents(span);
    sel.addRange(r2);
    syncPreview();
  };

  const getSelectionFontSize = () => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return 18;
    const node = sel.anchorNode;
    const el = node?.nodeType === 3 ? node.parentElement : node;
    return parseFloat(window.getComputedStyle(el)?.fontSize) || 18;
  };

  const adjustSize = (delta) => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount || sel.isCollapsed) return;
    const current = getSelectionFontSize();
    const next = Math.min(72, Math.max(8, Math.round(current + delta)));
    applySize(`${next}px`);
  };

  const clearFormat = () => {
    document.execCommand('removeFormat', false, null);
    syncPreview();
  };

  const handleSave = async () => {
    setLoading(true);
    setStatus('');
    const html = editorRef.current?.innerHTML || '';
    try {
      await announcementAPI.update({ text: html, is_active: isActive });
      setStatus('saved');
    } catch {
      setStatus('error');
    } finally {
      setLoading(false);
      setTimeout(() => setStatus(''), 3000);
    }
  };

  const hasContent = previewHtml.replace(/<[^>]*>/g, '').trim().length > 0;

  return (
    <div className="announcement-admin">
      <h2 className="announcement-admin-title">Announcement Bar</h2>
      <p className="announcement-admin-subtitle">
        Displays a banner directly below the navigation bar on the homepage.
      </p>

      {/* Live preview */}
      <div className="announcement-preview-section">
        <p className="announcement-preview-label">Preview</p>
        {isActive && hasContent
          ? (
            <div
              className="announcement-preview-bar"
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          ) : (
            <div className="announcement-preview-bar announcement-preview-hidden">
              <em>
                {!isActive
                  ? 'Bar is hidden — enable "Show on website" to display it.'
                  : 'Type some text below to see a preview.'}
              </em>
            </div>
          )
        }
      </div>

      {/* Form */}
      <div className="announcement-form-card">
        <div className="announcement-form-group">
          <label>Announcement text</label>

          {/* Formatting toolbar */}
          <div className="ann-toolbar">
            <button
              type="button"
              className="ann-tool ann-tool-bold"
              onMouseDown={hold}
              onClick={applyBold}
              title="Bold"
            >
              Bold
            </button>
            <div className="ann-tool-divider" />
            <button
              type="button"
              className="ann-tool ann-tool-size-dec"
              onMouseDown={hold}
              onClick={() => adjustSize(-2)}
              title="Decrease text size"
            >
              A−
            </button>
            <button
              type="button"
              className="ann-tool ann-tool-size-inc"
              onMouseDown={hold}
              onClick={() => adjustSize(2)}
              title="Increase text size"
            >
              A+
            </button>
            <div className="ann-tool-divider" />
            <button
              type="button"
              className="ann-tool"
              onMouseDown={hold}
              onClick={clearFormat}
              title="Remove formatting from selected text"
            >
              Clear
            </button>
            <span className="ann-toolbar-hint">Select text first, then apply formatting</span>
          </div>

          <div
            ref={editorRef}
            className="ann-editor"
            contentEditable
            suppressContentEditableWarning
            onInput={syncPreview}
            data-placeholder="e.g. FREE delivery on orders over £30 · Book a table: 01224 732333"
          />
        </div>

        <label className="announcement-checkbox-label">
          <input
            type="checkbox"
            checked={isActive}
            onChange={e => setIsActive(e.target.checked)}
          />
          <span>Show on website</span>
        </label>

        <div className="announcement-form-actions">
          {status === 'saved' && <span className="announcement-status-ok">Saved!</span>}
          {status === 'error' && <span className="announcement-status-err">Failed to save</span>}
          <button className="announcement-save-btn" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementAdmin;
